/**
 * DiaryPage — Gensolve-style clinic diary
 *
 * Columnar daily view with time grid, colour-coded appointment blocks,
 * blocked times (lunch/breaks), calendar navigation, and click-through
 * to patient files or scribe.
 *
 * This is the primary landing page for clinicians.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, RefreshCw, Calendar, AlertTriangle,
  FileText, Mic, User
} from 'lucide-react';
import API_BASE_URL from '../config/api';
import { useCurrentPatient } from '../contexts/CurrentPatientContext';
import { useClinic } from '../contexts/ClinicContext';

// ─── Time grid config ─────────────────────────────────────────────
const DEFAULT_START_HOUR = 7;   // 7 AM default
const DEFAULT_END_HOUR = 18;    // 6 PM default
const SLOT_HEIGHT = 4;          // px per minute
const HOUR_HEIGHT = SLOT_HEIGHT * 60; // 240px per hour

// ─── Appointment type colours (Gensolve-inspired) ─────────────────
const TYPE_COLORS = {
  initial:     { bg: '#FFF9C4', border: '#F9A825', text: '#5D4037' }, // Yellow — new patient
  follow_up:   { bg: '#C8E6C9', border: '#43A047', text: '#1B5E20' }, // Green — follow-up
  acc_initial: { bg: '#BBDEFB', border: '#1976D2', text: '#0D47A1' }, // Blue — ACC initial
  acc_fup:     { bg: '#C8E6C9', border: '#43A047', text: '#1B5E20' }, // Green — ACC follow-up
  impairment:  { bg: '#E1BEE7', border: '#8E24AA', text: '#4A148C' }, // Purple — impairment
  telehealth:  { bg: '#B3E5FC', border: '#0288D1', text: '#01579B' }, // Light blue — telehealth
  private:     { bg: '#FFE0B2', border: '#EF6C00', text: '#BF360C' }, // Orange — private
  blocked:     { bg: '#E0E0E0', border: '#9E9E9E', text: '#616161' }, // Grey — lunch/break
  default:     { bg: '#FFF9C4', border: '#F9A825', text: '#5D4037' }, // Yellow — default
};

// ─── Clinic badge colours ────────────────────────────────────────
const CLINIC_BADGES = {
  'anglesea-sports-medicine': { label: 'HAM', bg: '#EF6C00', text: '#fff' },
  'cambridge-sports-medicine': { label: 'CAM', bg: '#1976D2', text: '#fff' },
};

// Map a Gensolve SITE_NAME (free-text city) to a clinic badge so we can show
// the clinic origin on appointments that aren't yet linked to a ClinHx patient.
function siteNameToBadge(siteName) {
  if (!siteName) return null;
  const s = siteName.toLowerCase();
  if (s.includes('hamilton')) return CLINIC_BADGES['anglesea-sports-medicine'];
  if (s.includes('cambridge')) return CLINIC_BADGES['cambridge-sports-medicine'];
  return null;
}

function detectAppointmentType(appt) {
  const name = (appt.clientName || '').toLowerCase();
  const type = (appt.appointmentType || '').toLowerCase();
  const combined = `${name} ${type}`;

  if (combined.includes('impairment') || combined.includes('acc imp')) return 'impairment';
  if (combined.includes('rpi') || combined.includes('private initial')) return 'private';
  if (combined.includes('rfup') || combined.includes('fup') || combined.includes('follow')) return 'follow_up';
  if (combined.includes('riacc') || combined.includes('acc initial') || combined.includes('initial')) return 'initial';
  if (combined.includes('telehealth') || combined.includes('tele')) return 'telehealth';
  if (combined.includes('acc')) return 'acc_initial';
  return 'default';
}

function parsePatientNameAndType(clientName) {
  if (!clientName) return { name: 'Unknown', typeLabel: '' };
  // Gensolve format: "John Smith ACC GP1" or "RFUP - REG ACC FUP"
  // Try to split name from type code
  const parts = clientName.split(/\s+(ACC|RFUP|RIACC|RPI|FUP|GP\d|REG|Insured|NO ALL)/i);
  const name = parts[0]?.trim() || clientName;
  const typeLabel = parts.length > 1 ? clientName.substring(name.length).trim() : '';
  return { name, typeLabel };
}

// ─── Mini Calendar Component ──────────────────────────────────────

function MiniCalendar({ selectedDate, onSelectDate }) {
  const parsed = new Date(selectedDate + 'T12:00:00Z');
  const selYear = parsed.getUTCFullYear(), selMonth = parsed.getUTCMonth(), selDay = parsed.getUTCDate();
  const [viewYear, setViewYear] = useState(selYear);
  const [viewMonth, setViewMonth] = useState(selMonth);

  useEffect(() => { setViewYear(selYear); setViewMonth(selMonth); }, [selYear, selMonth]);

  const todayNZ = new Date().toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' });
  const [todayY, todayM, todayD] = todayNZ.split('-').map(Number);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const startOffset = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const changeMonth = (d) => {
    let m = viewMonth + d, y = viewYear;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setViewMonth(m); setViewYear(y);
  };

  const cells = [];
  for (let i = startOffset - 1; i >= 0; i--) {
    let pm = viewMonth - 1, py = viewYear; if (pm < 0) { pm = 11; py--; }
    cells.push({ day: prevMonthDays - i, month: pm, year: py, adj: true });
  }
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, month: viewMonth, year: viewYear, adj: false });
  const rem = 7 - (cells.length % 7);
  if (rem < 7) { let nm = viewMonth + 1, ny = viewYear; if (nm > 11) { nm = 0; ny++; } for (let d = 1; d <= rem; d++) cells.push({ day: d, month: nm, year: ny, adj: true }); }

  const click = (c) => onSelectDate(`${c.year}-${String(c.month + 1).padStart(2, '0')}-${String(c.day).padStart(2, '0')}`);

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <button onClick={() => changeMonth(-1)} style={calNavBtn}><ChevronLeft size={16} /></button>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{monthNames[viewMonth]} {viewYear}</span>
        <button onClick={() => changeMonth(1)} style={calNavBtn}><ChevronRight size={16} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, textAlign: 'center' }}>
        {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => <div key={d} style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', padding: 2 }}>{d}</div>)}
        {cells.map((c, i) => {
          const isSel = c.year === selYear && c.month === selMonth && c.day === selDay;
          const isToday = c.year === todayY && c.month === (todayM - 1) && c.day === todayD;
          return (
            <button key={i} onClick={() => click(c)} style={{
              width: 28, height: 28, margin: '0 auto', fontSize: 12, fontWeight: isSel || isToday ? 600 : 400,
              color: isSel ? '#fff' : c.adj ? '#c4c8cf' : '#374151',
              backgroundColor: isSel ? '#2563eb' : 'transparent',
              border: isToday && !isSel ? '2px solid #2563eb' : '2px solid transparent',
              borderRadius: '50%', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>{c.day}</button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week day tabs ────────────────────────────────────────────────

function WeekTabs({ selectedDate, onSelectDate }) {
  const sel = new Date(selectedDate + 'T12:00:00Z');
  const dayOfWeek = (sel.getUTCDay() + 6) % 7; // Mon=0
  const monday = new Date(sel);
  monday.setUTCDate(monday.getUTCDate() - dayOfWeek);

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    days.push(d);
  }

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const todayNZ = new Date().toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' });

  return (
    <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
      <button onClick={() => {
        const prev = new Date(sel); prev.setUTCDate(prev.getUTCDate() - 7);
        onSelectDate(prev.toISOString().split('T')[0]);
      }} style={calNavBtn}><ChevronLeft size={16} /></button>

      {days.map((d, i) => {
        const dateStr = d.toISOString().split('T')[0];
        const isSelected = dateStr === selectedDate;
        const isToday = dateStr === todayNZ;
        const isWeekend = i >= 5;
        return (
          <button key={i} onClick={() => onSelectDate(dateStr)} style={{
            flex: 1, padding: '8px 4px', border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
            borderRadius: 6, cursor: 'pointer', textAlign: 'center',
            backgroundColor: isSelected ? '#2563eb' : isWeekend ? '#f8f9fa' : '#fff',
            color: isSelected ? '#fff' : isWeekend ? '#9ca3af' : '#374151',
            fontWeight: isSelected || isToday ? 600 : 400, fontSize: 13,
            position: 'relative'
          }}>
            <div>{dayNames[i]}</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{d.getUTCDate()}</div>
            {isToday && !isSelected && <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#2563eb', margin: '2px auto 0' }} />}
          </button>
        );
      })}

      <button onClick={() => {
        const next = new Date(sel); next.setUTCDate(next.getUTCDate() + 7);
        onSelectDate(next.toISOString().split('T')[0]);
      }} style={calNavBtn}><ChevronRight size={16} /></button>
    </div>
  );
}

// ─── Main DiaryPage ───────────────────────────────────────────────

function DiaryPage() {
  const navigate = useNavigate();
  const { setCurrentPatient } = useCurrentPatient();
  const { activeClinicId, clinicParam } = useClinic();

  const todayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' });
  const [date, setDate] = useState(todayStr());
  const [appointments, setAppointments] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState(null);

  const fetchDiary = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const url = `${API_BASE_URL}/diary?date=${date}${activeClinicId ? '&clinicId=' + activeClinicId : ''}`;
      const res = await axios.get(url);
      setAppointments(res.data.appointments || []);
      setBlockedTimes(res.data.blockedTimes || []);
      setWarning(res.data.warning || null);
    } catch (err) {
      console.error('Error fetching diary:', err);
      setAppointments([]); setBlockedTimes([]);
      setWarning('Failed to load diary. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [date, activeClinicId]);

  useEffect(() => { fetchDiary(); }, [fetchDiary]);

  // Auto-refresh every 5 min
  useEffect(() => {
    const interval = setInterval(() => fetchDiary(false), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDiary]);

  const formatDisplayDate = (ds) => {
    const d = new Date(ds + 'T00:00:00');
    return d.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatTimeNZ = (iso) => new Date(iso).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', hour: 'numeric', minute: '2-digit', hour12: true });

  const getMinuteOfDay = (iso) => {
    const d = new Date(iso);
    const hour = parseInt(d.toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', hour: 'numeric', hour12: false }));
    const minute = parseInt(d.toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', minute: '2-digit' }));
    return hour * 60 + minute;
  };

  const openPatientFile = (appt) => {
    if (!appt.patient) return;
    setCurrentPatient({ id: appt.patient.id, name: appt.clientName, nhi: appt.patient.nhi });
    navigate(`/patient-file/${appt.patient.id}?tab=letters`);
  };

  // Main diary-block click: jump straight to the patient's editable letter.
  // LettersPage resolves ?patientId= to the most-recent-editable letter and
  // opens it in edit mode for doctors. Falls back to the patient file if
  // there's nothing editable yet.
  //
  // For unlinked appointments (no ClinHx patient resolved by gensolveClientId
  // or the name fallback), route to the dashboard in "link mode" so the user
  // can find the existing ClinHx record and associate it with this Gensolve
  // client. After linking, future diary loads resolve instantly.
  const openLetter = (appt) => {
    if (appt.patient) {
      setCurrentPatient({ id: appt.patient.id, name: appt.clientName, nhi: appt.patient.nhi });
      navigate(`/letters?patientId=${appt.patient.id}`);
      return;
    }
    if (!appt.clientId) return;
    const { name } = parsePatientNameAndType(appt.clientName);
    const params = new URLSearchParams({
      search: name || appt.clientName || '',
      linkGensolveId: appt.clientId,
      linkGensolveName: appt.clientName || ''
    });
    navigate(`/dashboard?${params.toString()}`);
  };

  const openScribe = (appt) => {
    if (!appt.patient) return;
    setCurrentPatient({ id: appt.patient.id, name: appt.clientName, nhi: appt.patient.nhi });
    navigate(`/scribe`);
  };

  // Status badge helpers
  const statusColors = { draft: '#e5e7eb', pending_review: '#fef3c7', ready_to_send: '#dbeafe', sent: '#dcfce7' };
  const statusTextColors = { draft: '#374151', pending_review: '#92400e', ready_to_send: '#1e40af', sent: '#166534' };

  // Calculate grid hours dynamically based on appointments
  let dayStartHour = DEFAULT_START_HOUR;
  let dayEndHour = DEFAULT_END_HOUR;
  for (const appt of appointments) {
    if (!appt.startTime) continue;
    const startMin = getMinuteOfDay(appt.startTime);
    const endMin = startMin + (appt.duration || 15);
    const startH = Math.floor(startMin / 60);
    const endH = Math.ceil(endMin / 60);
    if (startH < dayStartHour) dayStartHour = startH;
    if (endH > dayEndHour) dayEndHour = endH;
  }
  for (const bt of blockedTimes) {
    const startMin = bt.recurring
      ? new Date(bt.startTime).getUTCHours() * 60 + new Date(bt.startTime).getUTCMinutes()
      : getMinuteOfDay(bt.startTime);
    const endMin = bt.recurring
      ? new Date(bt.endTime).getUTCHours() * 60 + new Date(bt.endTime).getUTCMinutes()
      : getMinuteOfDay(bt.endTime);
    const startH = Math.floor(startMin / 60);
    const endH = Math.ceil(endMin / 60);
    if (startH < dayStartHour) dayStartHour = startH;
    if (endH > dayEndHour) dayEndHour = endH;
  }

  const totalHours = dayEndHour - dayStartHour;
  const gridHeight = totalHours * HOUR_HEIGHT;

  return (
    <div style={{ padding: '16px 24px', minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#111827' }}>
            <Calendar size={22} style={{ verticalAlign: 'middle', marginRight: 8, color: '#2563eb' }} />
            {formatDisplayDate(date)}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setDate(todayStr())} style={{ ...smallBtn, backgroundColor: '#2563eb', color: '#fff' }}>Today</button>
          <button onClick={() => fetchDiary()} style={smallBtn}><RefreshCw size={14} /></button>
        </div>
      </div>

      {warning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, marginBottom: 12 }}>
          <AlertTriangle size={18} color="#d97706" />
          <span style={{ fontSize: 13, color: '#92400e' }}>{warning}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Left: Calendar */}
        <div style={{ width: 220, flexShrink: 0 }}>
          <MiniCalendar selectedDate={date} onSelectDate={setDate} />

          {/* Legend */}
          <div style={{ marginTop: 12, backgroundColor: '#fff', borderRadius: 8, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 6 }}>APPOINTMENT TYPES</div>
            {[
              { key: 'initial', label: 'Initial' },
              { key: 'follow_up', label: 'Follow-up' },
              { key: 'impairment', label: 'Impairment' },
              { key: 'telehealth', label: 'Telehealth' },
              { key: 'private', label: 'Private' },
              { key: 'blocked', label: 'Break / Lunch' },
            ].map(t => (
              <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <div style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: TYPE_COLORS[t.key].bg, border: `2px solid ${TYPE_COLORS[t.key].border}` }} />
                <span style={{ fontSize: 12, color: '#374151' }}>{t.label}</span>
              </div>
            ))}
          </div>

          {/* Summary */}
          {!loading && appointments.length > 0 && (
            <div style={{ marginTop: 12, backgroundColor: '#fff', borderRadius: 8, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontSize: 13, color: '#6b7280' }}>
              <div>{appointments.length} appointment{appointments.length !== 1 ? 's' : ''}</div>
              <div>{appointments.filter(a => a.patient).length} linked in ClinHx</div>
            </div>
          )}
        </div>

        {/* Right: Week tabs + Time grid */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <WeekTabs selectedDate={date} onSelectDate={setDate} />

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
              <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : appointments.length === 0 ? (
            <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: 48, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <Calendar size={40} color="#d1d5db" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: '#6b7280', margin: 0 }}>No appointments for this date</p>
            </div>
          ) : (
            <div style={{ display: 'flex', backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              {/* Time labels column */}
              <div style={{ width: 56, flexShrink: 0, borderRight: '1px solid #e5e7eb', position: 'relative', height: gridHeight }}>
                {Array.from({ length: totalHours }, (_, i) => (
                  <div key={i} style={{
                    position: 'absolute', top: i * HOUR_HEIGHT, left: 0, right: 0, height: HOUR_HEIGHT,
                    borderTop: i > 0 ? '1px solid #e5e7eb' : 'none',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
                    padding: '2px 6px 0 0', fontSize: 11, color: '#9ca3af', fontWeight: 500
                  }}>
                    {(dayStartHour + i) % 12 || 12}{(dayStartHour + i) < 12 ? 'am' : 'pm'}
                  </div>
                ))}
              </div>

              {/* Appointments column */}
              <div style={{ flex: 1, position: 'relative', height: gridHeight }}>
                {/* 15-minute grid lines */}
                {Array.from({ length: totalHours * 4 }, (_, i) => {
                  const isHour = i % 4 === 0;
                  const isHalf = i % 2 === 0;
                  return (
                    <div key={i} style={{
                      position: 'absolute', top: i * SLOT_HEIGHT * 15, left: 0, right: 0,
                      borderTop: isHour ? '1px solid #e0e0e0' : isHalf ? '1px dashed #ececec' : '1px dotted #f5f5f5',
                      height: 0
                    }} />
                  );
                })}

                {/* Blocked times */}
                {blockedTimes.map((bt, i) => {
                  const startMin = bt.recurring
                    ? new Date(bt.startTime).getUTCHours() * 60 + new Date(bt.startTime).getUTCMinutes()
                    : getMinuteOfDay(bt.startTime);
                  const endMin = bt.recurring
                    ? new Date(bt.endTime).getUTCHours() * 60 + new Date(bt.endTime).getUTCMinutes()
                    : getMinuteOfDay(bt.endTime);
                  const top = (startMin - dayStartHour * 60) * SLOT_HEIGHT;
                  const height = (endMin - startMin) * SLOT_HEIGHT;
                  if (top < 0 || top >= gridHeight) return null;
                  const colors = TYPE_COLORS.blocked;
                  return (
                    <div key={`bt-${i}`} style={{
                      position: 'absolute', top, left: 4, right: 4, height: Math.max(height, 20),
                      backgroundColor: colors.bg, borderLeft: `4px solid ${colors.border}`,
                      borderRadius: 4, padding: '4px 8px', fontSize: 12, color: colors.text,
                      fontWeight: 600, display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: 1
                    }}>
                      {bt.reason || 'Break'}
                    </div>
                  );
                })}

                {/* Appointment blocks — group by 15-min slot for side-by-side */}
                {(() => {
                  const apptData = appointments
                    .filter(a => a.startTime)
                    .map(appt => {
                      const startMin = getMinuteOfDay(appt.startTime);
                      const duration = appt.duration || 15;
                      // Round to nearest 15-min slot for grouping
                      const slotKey = Math.floor(startMin / 15) * 15;
                      return { ...appt, startMin, endMin: startMin + duration, slotKey };
                    })
                    .sort((a, b) => a.startMin - b.startMin);

                  // Group by 15-min slot and assign column (0=left, 1=right, max 2)
                  const slotCounts = {};
                  const withColumns = apptData.map(appt => {
                    if (!slotCounts[appt.slotKey]) slotCounts[appt.slotKey] = 0;
                    const col = slotCounts[appt.slotKey];
                    slotCounts[appt.slotKey]++;
                    const totalInSlot = apptData.filter(a => a.slotKey === appt.slotKey).length;
                    const totalCols = Math.min(totalInSlot, 2); // max 2 columns
                    return { ...appt, col: Math.min(col, 1), totalCols };
                  });

                  return withColumns.map((appt, i) => {
                    const top = (appt.startMin - dayStartHour * 60) * SLOT_HEIGHT;
                    const height = (appt.endMin - appt.startMin) * SLOT_HEIGHT;
                    if (top < 0 || top >= gridHeight) return null;

                    const apptType = detectAppointmentType(appt);
                    const colors = TYPE_COLORS[apptType] || TYPE_COLORS.default;
                    const { name, typeLabel } = parsePatientNameAndType(appt.clientName);
                    const isLinked = !!appt.patient;
                    const isClickable = isLinked || !!appt.clientId; // unlinked blocks now route to dashboard link-mode
                    // Prefer the linked ClinHx clinicId for the badge; fall back to Gensolve SITE_NAME so
                    // unlinked appointments still show clinic origin.
                    const clinicBadge = appt.patient?.clinicId
                      ? CLINIC_BADGES[appt.patient.clinicId]
                      : siteNameToBadge(appt.siteName);

                    // Position: split width for overlapping appointments
                    const colWidth = 100 / appt.totalCols;
                    const leftPct = appt.col * colWidth;

                    return (
                      <div key={appt.gensolveId || i}
                        onClick={() => openLetter(appt)}
                        title={!isLinked && isClickable ? 'Not linked to a ClinHx patient — click to find and link' : undefined}
                        style={{
                          position: 'absolute', top,
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${colWidth}% - 4px)`,
                          height: Math.max(height, 28),
                          backgroundColor: colors.bg,
                          borderLeft: `4px solid ${colors.border}`,
                          // Dashed outline visually flags unlinked appointments without changing colour scheme
                          outline: !isLinked ? '1px dashed rgba(0,0,0,0.35)' : 'none',
                          outlineOffset: -2,
                          borderRadius: 4, padding: '4px 8px',
                          cursor: isClickable ? 'pointer' : 'default',
                          overflow: 'hidden', zIndex: 2, transition: 'box-shadow 0.15s',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          display: 'flex', flexDirection: 'column', justifyContent: 'center',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={e => { if (isClickable) e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.2)'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.08)'; }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 4 }}>
                              {name}
                              {clinicBadge && (
                                <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3, backgroundColor: clinicBadge.bg, color: clinicBadge.text, flexShrink: 0, lineHeight: '14px' }}>
                                  {clinicBadge.label}
                                </span>
                              )}
                            </div>
                            {typeLabel && <div style={{ fontSize: 10, color: colors.text, opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{typeLabel}</div>}
                          </div>
                          {isLinked && height >= 50 && (
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0, marginLeft: 4 }}>
                              <button onClick={e => { e.stopPropagation(); openScribe(appt); }}
                                title="Open Scribe" style={apptActionBtn}>
                                <Mic size={12} />
                              </button>
                              <button onClick={e => { e.stopPropagation(); openPatientFile(appt); }}
                                title="Patient File" style={apptActionBtn}>
                                <FileText size={12} />
                              </button>
                            </div>
                          )}
                        </div>

                        {isLinked && height >= 40 && (
                          <div style={{ display: 'flex', gap: 3, marginTop: 2, alignItems: 'center' }}>
                            {appt.letterSummary && appt.letterSummary.total > 0 ? (
                              <>
                                {['draft', 'pending_review', 'ready_to_send', 'sent'].map(s => {
                                  const count = appt.letterSummary[s];
                                  if (!count) return null;
                                  return <span key={s} style={{ fontSize: 9, padding: '1px 4px', borderRadius: 6, backgroundColor: statusColors[s], color: statusTextColors[s], fontWeight: 600 }}>{count}</span>;
                                })}
                              </>
                            ) : (
                              <span title="No letter generated yet" style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: 700, letterSpacing: 0.2 }}>
                                NO LETTER
                              </span>
                            )}
                          </div>
                        )}

                        {height < 40 && (
                          <div style={{ fontSize: 10, color: colors.text, opacity: 0.6 }}>
                            {formatTimeNZ(appt.startTime)}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Style helpers ───────────────────────────────────────────────
const calNavBtn = { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' };
const smallBtn = { padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, backgroundColor: '#fff', color: '#374151' };
const apptActionBtn = { background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 4, padding: 3, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };

export default DiaryPage;
